/**
 * Pure bus-routing helpers. Buses come in two flavours:
 *
 *  - return: fed by post-fader sends from tracks (shared reverb/delay)
 *  - group:  tracks route their whole output into it (drum bus, vocal bus)
 *
 * v1 keeps the graph acyclic by construction: buses always output to master,
 * sends only target return buses, and only tracks (never buses) route to a
 * group. That makes feedback loops impossible without runtime cycle checks.
 */

export const BUS_TYPES = ['return', 'group'];
export const MASTER_OUTPUT = 'master';

export function clampSendLevel(value) {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(1, num));
}

/** Normalize one bus definition, dropping anything malformed. */
export function normalizeBus(bus) {
  const id = bus?.id;
  if (!id || typeof id !== 'string') return null;
  const type = BUS_TYPES.includes(bus?.type) ? bus.type : 'return';
  const volume = Number(bus?.volume);
  const pan = Number(bus?.pan);
  return {
    id,
    type,
    name: typeof bus?.name === 'string' && bus.name.trim()
      ? bus.name.trim()
      : (type === 'group' ? 'Group' : 'Return'),
    volume: Number.isFinite(volume) ? Math.max(0, Math.min(1.25, volume)) : 1,
    pan: Number.isFinite(pan) ? Math.max(-1, Math.min(1, pan)) : 0,
    fx: Array.isArray(bus?.fx) ? bus.fx : []
  };
}

/** Normalize a bus list, dropping invalid entries and duplicate ids. */
export function normalizeBuses(buses) {
  if (!Array.isArray(buses)) return [];
  const seen = new Set();
  const out = [];
  buses.forEach((bus) => {
    const normalized = normalizeBus(bus);
    if (!normalized || seen.has(normalized.id)) return;
    seen.add(normalized.id);
    out.push(normalized);
  });
  return out;
}

/** Sends may only target existing return buses; levels clamped to 0..1. */
export function normalizeSends(sends, buses) {
  const returnIds = new Set(
    (buses || []).filter((b) => b.type === 'return').map((b) => b.id)
  );
  const out = {};
  Object.entries(sends || {}).forEach(([busId, level]) => {
    if (!returnIds.has(busId)) return;
    const clamped = clampSendLevel(level);
    if (clamped > 0) out[busId] = clamped;
  });
  return out;
}

/**
 * Resolve a track's output target. Only existing group buses are valid;
 * anything else (missing bus, a return bus, a deleted group) falls back to
 * master so audio is never silently orphaned.
 */
export function resolveOutputTarget(outputId, buses) {
  if (!outputId || outputId === MASTER_OUTPUT) return MASTER_OUTPUT;
  const bus = (buses || []).find((b) => b.id === outputId);
  if (!bus || bus.type !== 'group') return MASTER_OUTPUT;
  return bus.id;
}

/** Avoid disconnecting a live output when reconciliation resolves the same node. */
export function routingTargetChanged(outputGain, target) {
  return Boolean(outputGain && target && outputGain._routedTo !== target);
}

/** Stable default name for a newly created bus, e.g. "Return 2" / "Group 1". */
export function nextBusName(type, buses) {
  const label = type === 'group' ? 'Group' : 'Return';
  const count = (buses || []).filter((b) => b.type === type).length;
  return `${label} ${count + 1}`;
}
